import type {
  ProgramSlideLayoutPatch,
  ProgramSlideLayoutV1,
  ProgramSlideQuizChoiceOptionV1,
  ProgramSlideQuizChoiceSetV1,
} from '../../types/programContentSlideCanvas';
import { QUIZ_DEFAULT_TEXT_COLOR_HEX } from '../../types/programContentSlideCanvas';

export const QUIZ_CHOICE_MIN_OPTIONS = 2;
export const QUIZ_CHOICE_MAX_OPTIONS = 8;

export function defaultQuizChoiceLabel(optionIndex: number): string {
  return `${optionIndex + 1}번`;
}

export function getQuizChoiceOptionsForSet(
  layout: ProgramSlideLayoutV1,
  setId: string
): ProgramSlideQuizChoiceOptionV1[] {
  return (layout.quizChoiceOptions ?? [])
    .filter((o) => o.setId === setId)
    .sort((a, b) => a.optionIndex - b.optionIndex);
}

export function mergeSlideLayoutArrays(
  current: ProgramSlideLayoutV1,
  next: ProgramSlideLayoutPatch
): ProgramSlideLayoutV1 {
  return {
    ...current,
    ...next,
    v: 1,
    clipboardImages: next.clipboardImages ?? current.clipboardImages ?? [],
    scanImages: next.scanImages ?? current.scanImages ?? [],
    shapes: next.shapes ?? current.shapes ?? [],
    polylines: next.polylines ?? current.polylines ?? [],
    tables: next.tables ?? current.tables ?? [],
    quizChoiceSets: next.quizChoiceSets ?? current.quizChoiceSets ?? [],
    quizChoiceOptions: next.quizChoiceOptions ?? current.quizChoiceOptions ?? [],
    webpages: next.webpages ?? current.webpages ?? [],
  };
}

export function createQuizChoiceSetBlocks(
  setId: string,
  zIndex: number,
  genId: (n: number) => string,
  existingCount: number
): { set: ProgramSlideQuizChoiceSetV1; options: ProgramSlideQuizChoiceOptionV1[] } {
  const labels = ['1번', '2번', '3번', '4번'];
  const baseX = 0.1;
  const baseY = 0.18;
  const sizeW = 0.1;
  const sizeH = 0.1;
  const gap = 0.012;
  const set: ProgramSlideQuizChoiceSetV1 = {
    id: setId,
    correctOptionIndices: [],
    zIndex,
  };
  const options: ProgramSlideQuizChoiceOptionV1[] = labels.map((label, i) => ({
    id: genId(existingCount + i),
    setId,
    optionIndex: i,
    label,
    x: baseX,
    y: baseY + i * (sizeH + gap),
    w: sizeW,
    h: sizeH,
      fontSizePx: 32,
      fontFamily: 'pretendard',
      colorHex: '#141414',
      backgroundColorHex: '#ffffff',
      bold: true,
      cornerRadiusPx: 8,
      zIndex,
    }));
  return { set, options };
}

export function quizChoiceResizeWithSyncedSize(
  opt: Pick<ProgramSlideQuizChoiceOptionV1, 'x' | 'y' | 'w' | 'h'>,
  newW: number,
  newH: number,
  corner: 'nw' | 'ne' | 'sw' | 'se'
): Pick<ProgramSlideQuizChoiceOptionV1, 'x' | 'y' | 'w' | 'h'> {
  const right = opt.x + opt.w;
  const bottom = opt.y + opt.h;
  if (corner === 'se') return { x: opt.x, y: opt.y, w: newW, h: newH };
  if (corner === 'sw') return { x: right - newW, y: opt.y, w: newW, h: newH };
  if (corner === 'ne') return { x: opt.x, y: bottom - newH, w: newW, h: newH };
  return { x: right - newW, y: bottom - newH, w: newW, h: newH };
}

export type QuizChoiceSetBBox = { left: number; top: number; right: number; bottom: number };

export function quizChoiceResizeSetFromCenter(
  options: ProgramSlideQuizChoiceOptionV1[],
  setId: string,
  setBBox: QuizChoiceSetBBox,
  newW: number,
  newH: number,
  startW: number,
  startH: number
): ProgramSlideQuizChoiceOptionV1[] {
  const bboxW = Math.max(0.0001, setBBox.right - setBBox.left);
  const bboxH = Math.max(0.0001, setBBox.bottom - setBBox.top);
  const cx = (setBBox.left + setBBox.right) / 2;
  const cy = (setBBox.top + setBBox.bottom) / 2;
  const scaleX = newW / Math.max(0.0001, startW);
  const scaleY = newH / Math.max(0.0001, startH);
  const newBboxW = bboxW * scaleX;
  const newBboxH = bboxH * scaleY;
  const newLeft = cx - newBboxW / 2;
  const newTop = cy - newBboxH / 2;
  const roundedW = Math.round(newW * 10000) / 10000;
  const roundedH = Math.round(newH * 10000) / 10000;
  return options.map((opt) => {
    if (opt.setId !== setId) return opt;
    const relX = (opt.x - setBBox.left) / bboxW;
    const relY = (opt.y - setBBox.top) / bboxH;
    return {
      ...opt,
      x: Math.round((newLeft + relX * newBboxW) * 10000) / 10000,
      y: Math.round((newTop + relY * newBboxH) * 10000) / 10000,
      w: roundedW,
      h: roundedH,
    };
  });
}

export function toggleQuizChoiceCorrectIndex(set: ProgramSlideQuizChoiceSetV1, index: number): number[] {
  const cur = [...(set.correctOptionIndices ?? [])];
  const pos = cur.indexOf(index);
  if (pos >= 0) cur.splice(pos, 1);
  else cur.push(index);
  return Array.from(new Set(cur))
    .filter((i) => i >= 0 && i < QUIZ_CHOICE_MAX_OPTIONS)
    .sort((a, b) => a - b);
}

export function remapQuizChoiceCorrectIndicesAfterRemove(
  indices: number[],
  removedIndex: number
): number[] {
  return indices
    .filter((i) => i !== removedIndex)
    .map((i) => (i > removedIndex ? i - 1 : i))
    .filter((i) => i >= 0 && i < QUIZ_CHOICE_MAX_OPTIONS)
    .sort((a, b) => a - b);
}

export function createAddedQuizChoiceOption(
  setId: string,
  existing: ProgramSlideQuizChoiceOptionV1[],
  genId: (n: number) => string,
  seed: number
): ProgramSlideQuizChoiceOptionV1 {
  const sorted = [...existing].sort((a, b) => a.optionIndex - b.optionIndex);
  const last = sorted[sorted.length - 1];
  const gap = 0.012;
  const nextIndex = sorted.length;
  return {
    id: genId(seed),
    setId,
    optionIndex: nextIndex,
    label: defaultQuizChoiceLabel(nextIndex),
    x: last?.x ?? 0.1,
    y: last ? Math.min(0.92, last.y + last.h + gap) : 0.18,
    w: last?.w ?? 0.1,
    h: last?.h ?? 0.1,
    fontSizePx: last?.fontSizePx ?? 32,
    fontFamily: last?.fontFamily ?? 'pretendard',
    colorHex: last?.colorHex ?? '#141414',
    backgroundColorHex: last?.backgroundColorHex ?? '#ffffff',
    bold: last?.bold ?? true,
    cornerRadiusPx: last?.cornerRadiusPx ?? 8,
    zIndex: last?.zIndex ?? 0,
  };
}

export const QUIZ_CHOICE_DEFAULT_LABELS = ['1번', '2번', '3번', '4번'] as const;

export function quizChoiceOptionLabelColor(): string {
  return QUIZ_DEFAULT_TEXT_COLOR_HEX;
}
